import React, { useContext, useState } from 'react';
import { Alert, Button, Input, message, Select, Typography } from 'antd';
import { ApplicationUIContext, Drawer, DrawerContent, Form } from '@yuhuinc/ui-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import { resetApplicationStatuses } from '../../store/actions';
import { connect } from 'react-redux';
import { CurrentApplicationContext } from '../../ApplicationOverview';
import { apiDeleteRequest } from 'helpers/ApiHelper';

const { Text } = Typography;
const { TextArea } = Input;

const drawerWidth = 378;
const textAreaRows = 3;
const mobileFontSize = 16;

const StyledFormItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const StyledButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  column-gap: 8px;
`;

const requiredFieldRule = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const RemoveApplicant = ({
  removeApplicantDrawer,
  applicantToRemove,
  resetApplicationStatuses,
}) => {
  const [removeApplicantForm] = Form.useForm();
  const [hasError, setHasError] = useState(false);
  const { isMobile } = useContext(ApplicationUIContext);
  const application = useContext(CurrentApplicationContext);
  const queryClient = useQueryClient();

  const removeApplicantMutation = useMutation({
    mutationFn: (params) => {
      const { applicationId, applicantId, occupantId, payload } = params;
      const applicantsOrOccupants = occupantId ? 'occupants' : 'applicants';
      const applicantOrOccupantId = occupantId ? occupantId : applicantId;
      return apiDeleteRequest(
        `/landlords/application_management/applications/${applicationId}/${applicantsOrOccupants}/${applicantOrOccupantId}`,
        payload
      );
    },
    onSuccess: () => {
      message.success(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.applicantRemoved'
        )
      );
      resetApplicationStatuses();
      removeApplicantDrawer.close();
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: () => message.error(getTranslatedString('unknownError')),
  });

  const allApplicants = application?.applicants;

  const isLeadApplicant = applicantToRemove?.applicant_type === 'lead_applicant';
  const isOccupant = applicantToRemove?.applicant_type === 'occupant';

  const maybeRenderAdditionalQuestions = (options) =>
    isLeadApplicant && (
      <Form.Item
        label={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.selectANewLeadApplicant'
        )}
        name="replace_with_cotenant_id"
        rules={[requiredFieldRule]}
      >
        <Select size={isMobile ? 'large' : 'middle'}>
          {options.map((applicant) => (
            <Select.Option key={applicant.id} value={applicant.id}>
              {applicant.full_name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );

  const submitButtonText = () =>
    isLeadApplicant
      ? getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.removeAndAssignNewLead'
        )
      : getTranslatedString('remove');

  const drawerTitle = () =>
    isLeadApplicant
      ? getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.removeLeadApplicant'
        )
      : getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.removeApplicant'
        );

  const handleOnSubmit = ({
    applicant_id_to_remove,
    reason_for_removal,
    replace_with_cotenant_id,
  }) => {
    const applicantOrOccupantId = isOccupant
      ? { occupantId: applicant_id_to_remove }
      : { applicantId: applicant_id_to_remove };

    removeApplicantMutation.mutate({
      applicationId: application?.id,
      ...applicantOrOccupantId,
      payload: {
        reason_for_removal,
        replace_with_cotenant_id,
      },
    });
  };

  // Show everyone but the applicant to be removed
  const options =
    (allApplicants &&
      allApplicants
        ?.map(({ id, full_name }) => ({ id, full_name }))
        .filter((option) => option.id !== applicantToRemove?.id)) ||
    [];

  const resetFormAndCloseDrawer = () => {
    setHasError(false);
    removeApplicantForm.resetFields();
    removeApplicantDrawer.close();
  };

  const onError = ({ errorFields }) => {
    if (errorFields.length > 1) {
      setHasError(true);
    }
  };

  const size = isMobile ? 'large' : 'middle';

  return (
    <Drawer
      title={drawerTitle()}
      visible={removeApplicantDrawer?.isVisible}
      closable
      destroyOnClose
      onClose={resetFormAndCloseDrawer}
      width={drawerWidth}
    >
      <DrawerContent>
        {hasError && (
          <Alert message={getTranslatedString('errorsOnThisPage')} type="error" showIcon />
        )}

        <Text style={isMobile && { fontSize: mobileFontSize }}>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.warning'
          )}
        </Text>

        <Form
          layout="vertical"
          scrollToFirstError
          form={removeApplicantForm}
          onFinish={handleOnSubmit}
          onFinishFailed={onError}
          initialValues={{ applicant_id_to_remove: applicantToRemove?.id }}
        >
          <StyledFormItemContainer>
            <Form.Item
              label="Applicant to remove from application"
              name="applicant_id_to_remove"
              hidden
            />
            <Form.Item
              label={getTranslatedString(
                'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.reasonForRemoval'
              )}
              name="reason_for_removal"
              rules={[requiredFieldRule]}
            >
              <TextArea rows={textAreaRows} size={size} />
            </Form.Item>
            {maybeRenderAdditionalQuestions(options)}
          </StyledFormItemContainer>
          <StyledButtonContainer>
            <Button size={size} type="secondary" onClick={resetFormAndCloseDrawer}>
              {getTranslatedString('cancel')}
            </Button>
            <Button
              size={size}
              type="primary"
              htmlType="submit"
              loading={removeApplicantMutation.isLoading}
            >
              {submitButtonText()}
            </Button>
          </StyledButtonContainer>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

const mapDispatchToProps = {
  resetApplicationStatuses,
};

export default connect(null, mapDispatchToProps)(RemoveApplicant);
